<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div class="header-title">
            Shared Id Search
          </div>
        </template>
      </es-header>
    </div>
    <es-loader
      v-if="loading"
      style="margin: 4.5rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="1rem"
      style="padding: 5rem"
    />
    <div v-else>
      <div class="no-border-top">
        <div class="no-bottom-border">
          <es-form>
            <template #es-form>
              <div class="row removeUnderline">
                <div class="field is-horizontal">
                  <div class="field-label is-normal">
                    <label class="label">
                      Person Type
                    </label>
                  </div>
                  <div class="field-body">
                    <div class="field">
                      <div class="control">
                        <div class="buttons has-addons">
                          <button
                            v-for="(item, index) in sortedPersonTypesOptions"
                            class="button button--form"
                            style="min-width: 100px"
                            :class="{'is-active': personType === item.code}"
                            :key="index"
                            @click="personType = item.code"
                          >
                            {{ item.name }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row removeUnderline">
                <div class="field is-horizontal">
                  <div class="field-label is-normal">
                    <label class="label">
                      Field to Search
                    </label>
                  </div>
                  <div class="field-body">
                    <div class="field">
                      <div class="control">
                        <es-select
                          keyValue="code"
                          ref="resetFields"
                          valueTpl="${description}"
                          :allowUserInput="false"
                          :clearOut="true"
                          :errorMsg="msgFields"
                          :hasError="hasErrorFields"
                          :multi="true"
                          :preSelected="preSelectedFields"
                          :search="true"
                          :setValues="fieldOptions"
                          @update:values="selectedFields = $event"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row removeUnderline">
                <div class="field is-horizontal">
                  <div class="field-label is-normal">
                    <label class="label">
                      Search Type
                    </label>
                  </div>
                  <div class="field-body">
                    <div class="field">
                      <div class="control">
                        <div class="buttons has-addons">
                          <div>
                            SQL (Exact)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </es-form>
        </div>
      </div>
      <div
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 5.15rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          style="margin-right: 1rem"
          @click="reset"
        >
          Reset
        </button>
        <button
          class="button is-primary"
          @click="validateFieldAndUpdateRoute"
        >
          Search
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import NotificationManager from '@/mixins/NotificationManager';

export default {
  mixins: [
    NotificationManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  data() {
    return this.initialData();
  },
  created() {
    this.onLoad();
  },
  watch: {
    $route() {
      this.search();
    },
    personType() {
      this.reset(false);
      this.updateSearchFieldsAndLabels();
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activePersonTypes',
      'defaultPersonType',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
      'getFieldLabelByPersonType',
    ]),
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    searchParams() {
      let fields = null;
      if (this.$route.query.fields) {
        fields = Array.isArray(this.$route.query.fields)
          ? this.$route.query.fields.map(field => `&fields=${field}`).join('')
          : `&fields=${this.$route.query.fields}`;
      } else {
        fields = this.selectedFields.map(field => `&fields=${field.code}`).join('');
      }
      const pageNum = `&page=${this.$route.query.page || 1}`;
      if (this.$route.query.personType || this.personType) {
        const personTypeString = `&personType=${this.$route.query.personType || this.personType}`;
        return fields + pageNum + personTypeString;
      }
      return fields + pageNum;
    },
    sortedPersonTypesOptions() {
      const sorted = this.userPersonTypes.slice();
      if (!this.contextMode) {
        sorted.unshift({ name: 'All', code: null });
      }
      const compareNames = (a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      };
      return sorted.sort(compareNames);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadPersonTypes',
    ]),
    fieldEnabled(code) {
      const type = this.personType === null ? this.defaultPersonType : this.personType;
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', type);
    },
    fieldLabel(code) {
      const type = this.personType === null ? this.defaultPersonType : this.personType;
      return this.getFieldLabelByPersonType(code, 'ASSIGN', type);
    },
    initialData() {
      return {
        commaSeparatedString: [],
        fieldOptions: [],
        formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
        hasErrorFields: false,
        loading: false,
        loadingError: false,
        msgFields: null,
        personType: null,
        preSelectedFields: [],
        searchResults: null,
        selectedFields: [],
        userPersonTypesCodes: null,
      };
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;

      try {
        await Promise.all([
          this.loadGenders(),
          this.loadPersonTypes(),
        ]);

        const { query } = this.$route;
        this.userPersonTypesCodes = this.userPersonTypes.map(item => item.code);

        // Determine personType based on query or default logic
        if (query.personType) {
          this.personType = query.personType;
        } else if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
          this.personType = this.defaultPersonType;
        } else {
          [this.personType] = this.userPersonTypesCodes;
        }

        this.updateSearchFieldsAndLabels();

        if (query.fields) {
          this.preSelectedFields = this.fieldOptions
            .filter(option => query.fields.includes(option.code));

          this.selectedFields = [...this.preSelectedFields];

          if (query.personType !== undefined) {
            this.personType = query.personType;
          } else if (!this.contextMode) {
            this.personType = null;
          }

          this.search();
        }
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    reset(resetPersonType = true) {
      if (this.selectedFields.length && this.$refs.resetFields) {
        this.$refs.resetFields.clear();
      }
      this.hasErrorFields = false;
      this.msgFields = null;
      this.resetNotifications();
      if (resetPersonType) {
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
        this.$emit('sharedIdResults', { searchResults: null, selectedPersonType: null, totalCount: null });
      }
    },
    resetNotifications() {
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    async search() {
      this.$emit('loading', true);
      this.$emit('loadingError', false);

      try {
        const response = await axios.post(`idmanager/sharedids?${this.searchParams}`);
        const { data } = response;
        const { data: searchResults, totalCount } = data;
        const selectedPersonType = this.personType;

        this.$emit('sharedIdResults', { searchResults, selectedPersonType, totalCount });
      } catch (error) {
        this.showError(error);
        this.$emit('loadingError', true);
      } finally {
        this.$emit('loading', false);
      }
    },
    showError(error) {
      this.showNotification('danger', [error]);
    },
    updateSearchFieldsAndLabels() {
      try {
        const temp = this.properties['PERSON-PID_SHAREDID_FIELDS'];
        this.commaSeparatedString = temp ? temp.split(',').map(item => item.trim()) : [];
        const allFields = this.commaSeparatedString.map((element) => {
          const description = this.fieldLabel(element);
          const enabled = this.fieldEnabled(element);
          return {
            code: element,
            description,
            enabled,
          };
        });
        this.fieldOptions = allFields
          .filter(field => field.enabled)
          .map((field) => {
            const rest = Object.assign({}, field);
            delete rest.enabled;
            return rest;
          });
        return this.fieldOptions;
      } catch (error) {
        throw error;
      }
    },
    validateFieldAndUpdateRoute() {
      if (this.selectedFields.length === 0) {
        this.hasErrorFields = true;
        this.msgFields = 'Field to Search is required.';
        this.showError(this.formErrorMsg);
        return;
      }
      this.hasErrorFields = false;
      this.msgFields = null;
      this.resetNotifications();
      this.$router.push({
        query: {
          fields: this.selectedFields.map(a => a.code),
          page: 1,
          personType: this.personType,
        },
      });
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
